import type React from "react"

import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"
import { Pause, Play } from "lucide-react"
import { useEffect, useRef, useState } from "react"

interface AudioPlayerProps {
  audioUrl: string
  className?: string
}

export function AudioPlayer({ audioUrl, className = "" }: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [playbackRate, setPlaybackRate] = useState(1)
  const [waveformData, setWaveformData] = useState<number[]>([])
  const [isLoaded, setIsLoaded] = useState(false)
  const [isDragging, setIsDragging] = useState(false)



  // Initialize audio element
  useEffect(() => {
    const audio = new Audio(audioUrl)
    audioRef.current = audio

    const handleLoadedMetadata = () => {
      setDuration(audio.duration)
      setIsLoaded(true)
      if (!waveformData.length) {
        generateWaveformData()
      }
    }

    const handleTimeUpdate = () => {
      if (!isDragging) {
        setCurrentTime(audio.currentTime)
      }
    }

    const handleEnded = () => {
      setIsPlaying(false)
      setCurrentTime(audio.duration)
    }

    audio.addEventListener("loadedmetadata", handleLoadedMetadata)
    audio.addEventListener("timeupdate", handleTimeUpdate)
    audio.addEventListener("ended", handleEnded)

    return () => {
      audio.pause()
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata)
      audio.removeEventListener("timeupdate", handleTimeUpdate)
      audio.removeEventListener("ended", handleEnded)
    }
  }, [audioUrl, isDragging])

  // Generate random waveform data for visualization
  // In a real app, you would analyze the actual audio file
  const generateWaveformData = () => {
    const sampleCount = 60
    const data = Array.from({ length: sampleCount }, () => Math.random() * 0.7 + 0.15)
    setWaveformData(data)
  }

  // Draw waveform visualization
  useEffect(() => {
    if (!canvasRef.current || waveformData.length === 0) return

    const canvas = canvasRef.current
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const dpr = window.devicePixelRatio || 1
    const rect = canvas.getBoundingClientRect()

    canvas.width = rect.width * dpr
    canvas.height = rect.height * dpr
    ctx.scale(dpr, dpr)

    ctx.clearRect(0, 0, rect.width, rect.height)

    const barWidth = Math.max(2, rect.width / waveformData.length - 1)
    const barMaxHeight = rect.height
    const barRadius = 1 // Radius for rounded corners

    // Calcula a posição exata do progresso em relação ao número de barras
    const progressPosition = (currentTime / duration) * waveformData.length

    waveformData.forEach((value, index) => {
      const x = index * (barWidth + 1)
      const height = value * barMaxHeight
      const y = (rect.height - height) / 2

      // Determina se a barra deve ser colorida baseado na posição exata do progresso
      if (index < progressPosition) {
        ctx.fillStyle = "#3b82f6" // Blue for played portion
      } else {
        ctx.fillStyle = "#6b7280" // Gray for unplayed portion
      }

      // Draw rounded rectangle
      roundedRect(ctx, x, y, barWidth, height, barRadius)
    })

    // Draw playhead dot
    if (duration > 0) {
      const progress = currentTime / duration
      const dotX = progress * (rect.width - barWidth)
      const dotY = rect.height / 2

      ctx.beginPath()
      ctx.arc(dotX, dotY, 4, 0, Math.PI * 2)
      ctx.fillStyle = "#3b82f6"
      ctx.fill()
    }
  }, [waveformData, currentTime, duration])

  // Helper function to draw rounded rectangles
  function roundedRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
  ) {
    ctx.beginPath()
    ctx.moveTo(x + radius, y)
    ctx.lineTo(x + width - radius, y)
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius)
    ctx.lineTo(x + width, y + height - radius)
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height)
    ctx.lineTo(x + radius, y + height)
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius)
    ctx.lineTo(x, y + radius)
    ctx.quadraticCurveTo(x, y, x + radius, y)
    ctx.closePath()
    ctx.fill()
  }

  // Handle play/pause
  const togglePlayPause = () => {
    if (!audioRef.current) return

    if (isPlaying) {
      audioRef.current.pause()
    } else {
      audioRef.current.play()
    }

    setIsPlaying(!isPlaying)
  }

  // Handle seeking by clicking on the waveform
  const handleWaveformClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!audioRef.current || !canvasRef.current) return

    const rect = canvasRef.current.getBoundingClientRect()
    const clickX = e.clientX - rect.left
    const seekPosition = (clickX / rect.width) * duration

    audioRef.current.currentTime = seekPosition
    setCurrentTime(seekPosition)
  }

  // Handle mouse down on waveform for dragging
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDragging(true)
    handleWaveformClick(e)

    document.addEventListener("mousemove", handleMouseMove)
    document.addEventListener("mouseup", handleMouseUp)
  }

  // Handle mouse move during drag
  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || !canvasRef.current || !audioRef.current) return

    const rect = canvasRef.current.getBoundingClientRect()
    const clickX = Math.max(0, Math.min(e.clientX - rect.left, rect.width))
    const seekPosition = (clickX / rect.width) * duration

    setCurrentTime(seekPosition)
  }

  // Handle mouse up to end dragging
  const handleMouseUp = () => {
    if (isDragging && audioRef.current) {
      audioRef.current.currentTime = currentTime
    }

    setIsDragging(false)
    document.removeEventListener("mousemove", handleMouseMove)
    document.removeEventListener("mouseup", handleMouseUp)
  }

  // Handle playback rate change
  const changePlaybackRate = (rate: number) => {
    if (!audioRef.current) return

    audioRef.current.playbackRate = rate
    setPlaybackRate(rate)
  }

  // Format time in mm:ss
  const formatTime = (timeInSeconds: number) => {
    const minutes = Math.floor(timeInSeconds / 60)
    const seconds = Math.floor(timeInSeconds % 60)
    return `${minutes}:${seconds.toString().padStart(2, "0")}`
  }

  return (
    <div ref={containerRef} className={cn(`p-2 rounded-lg bg-card shadow-sm flex items-center gap-3`, className)}>
      {/* Play/Pause button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={togglePlayPause}
        disabled={!isLoaded}
        aria-label={isPlaying ? "Pause" : "Play"}
        className="text-blue-500 hover:text-blue-600 hover:bg-transparent p-1"
      >
        {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
      </Button>

      {/* Current time */}
      <span className="text-xs text-gray-400 min-w-[32px]">{formatTime(currentTime)}</span>

      {/* Waveform with integrated progress */}
      <div className="flex-grow h-8 relative">
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full cursor-pointer"
          onClick={handleWaveformClick}
          onMouseDown={handleMouseDown}
          aria-label="Audio waveform progress"
          role="slider"
          aria-valuemin={0}
          aria-valuemax={duration}
          aria-valuenow={currentTime}
        />
      </div>

      {/* Total duration */}
      <span className="text-xs text-gray-400 min-w-[32px]">{formatTime(duration)}</span>

      {/* Playback speed (hidden in dropdown) */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="text-xs text-gray-400 hover:bg-transparent p-1">
            {playbackRate}x
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {[0.5, 0.75, 1, 1.25, 1.5, 2].map((rate) => (
            <DropdownMenuItem
              key={rate}
              onClick={() => changePlaybackRate(rate)}
              className={playbackRate === rate ? "bg-muted" : ""}
            >
              {rate}x
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

